import {Banner} from '@primer/react'
import {Suspense, useCallback} from 'react'
import {graphql, useLazyLoadQuery, usePaginationFragment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import type {LazyRelationshipsRelatesToListViewFragment$data} from './__generated__/LazyRelationshipsRelatesToListViewFragment.graphql'
import type {LazyRelationshipsRelatesToListViewQuery} from './__generated__/LazyRelationshipsRelatesToListViewQuery.graphql'
import {
  PAGE_SIZE,
  type PaginatedRelationshipsListAllViewItemFragment,
  RelationshipsListAllViewPaginated,
  RelationshipsListItem,
  RelationshipsListViewLoadingSkeleton,
} from './PaginatedRelationshipsListAllView'

// this connection string has to match the @connection string defined in the fragment below
export const RELATES_TO_LIST_ALL_RELAY_CONNECTION = 'LazyRelationshipsRelatesToListViewFragment__relatesTo'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const LazyRelationshipsRelatesToListViewFragment = graphql`
  fragment LazyRelationshipsRelatesToListViewFragment on Issue
  @refetchable(queryName: "LazyRelationshipsRelatesToListViewPaginatedQuery")
  @argumentDefinitions(cursor: {type: "String", defaultValue: null}, pageSize: {type: "Int"}) {
    repository {
      nameWithOwner
    }
    relatesTo(first: $pageSize, after: $cursor)
      @connection(key: "LazyRelationshipsRelatesToListViewFragment__relatesTo") {
      edges {
        node {
          id
          url
          number
          titleHTML
          state
          stateReason
          repository {
            name
            nameWithOwner
            owner {
              login
            }
          }
          assignees(first: 10) {
            totalCount
            edges {
              node {
                id
                login
                avatarUrl
              }
            }
          }
          closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
            totalCount
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const LazyRelationshipsRelatesToListViewQueryGraphQL = graphql`
  query LazyRelationshipsRelatesToListViewQuery($id: ID!, $cursor: String, $pageSize: Int) {
    issue: node(id: $id) {
      ...LazyRelationshipsRelatesToListViewFragment
        @dangerously_unaliased_fixme
        @arguments(cursor: $cursor, pageSize: $pageSize)
    }
  }
`

export type LazyRelationshipsRelatesToListViewProps = {
  itemId: string
  pageSize?: number
}
export function LazyRelationshipsRelatesToListView({
  itemId,
  pageSize = PAGE_SIZE,
}: LazyRelationshipsRelatesToListViewProps) {
  const loadingSkeletonComponent = <RelationshipsListViewLoadingSkeleton />

  return (
    <Suspense fallback={loadingSkeletonComponent}>
      <RelationshipsRelatesToListViewInternal itemId={itemId} pageSize={pageSize} />
    </Suspense>
  )
}

type RelationshipsRelatesToListViewInternalProps = {
  itemId: string
  pageSize: number
}
function RelationshipsRelatesToListViewInternal({itemId, pageSize}: RelationshipsRelatesToListViewInternalProps) {
  const queryData = useLazyLoadQuery<LazyRelationshipsRelatesToListViewQuery>(
    LazyRelationshipsRelatesToListViewQueryGraphQL,
    {id: itemId, pageSize},
  )

  const issueFragment = queryData.issue
  const {data, loadNext, isLoadingNext} = usePaginationFragment(
    LazyRelationshipsRelatesToListViewFragment,
    issueFragment,
  )
  const issueData = data as LazyRelationshipsRelatesToListViewFragment$data | null
  const onLoadMore = useCallback(() => loadNext(PAGE_SIZE), [loadNext])

  const emptyBanner = (
    <Banner variant="info">
      <Banner.Title>{LABELS.emptyRelatesToList}</Banner.Title>
    </Banner>
  )

  if (!issueFragment || !issueData) return emptyBanner

  const repository = issueData.repository
  if (!repository) return emptyBanner

  const nodes = issueData.relatesTo?.edges?.map(edge => edge?.node).filter(node => !!node)
  if (!nodes || nodes.length === 0) return emptyBanner

  // `relatesTo` is permission-filtered server-side, so there's no inaccessible-issues notice (unlike blocked-by).
  return (
    <RelationshipsListAllViewPaginated
      accessibleTitle={LABELS.relationNames.relatesToIssues}
      isLoadingNext={isLoadingNext}
      hasNextPage={issueData.relatesTo?.pageInfo?.hasNextPage}
      onLoadMore={onLoadMore}
    >
      {nodes.map(node => (
        <RelationshipsListItem
          key={node.id}
          // RELAY-CLEANUP(LazyRelationshipsRelatesToListView): remove cast when this view leaves Relay
          issue={node as PaginatedRelationshipsListAllViewItemFragment}
          sourceIssueRepositoryNameWithOwner={repository.nameWithOwner}
        />
      ))}
    </RelationshipsListAllViewPaginated>
  )
}
